/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ModelResponse, nullify, ResponseData} from '@dvrd/dvr-controls';
import {CustomerType, EmailActivity, EmailFlow, Fulfilment} from '../util/interfaces';
import {
    CreateEmailStep, createEmailStep, deleteEmailStep, getAdminEmailSteps, getEmailStep, getEmailSteps, UpdateEmailStep,
    updateEmailStep
} from '../util/requestSender';
import {deepClone} from '../util/utils';
import Account from './accountModel';
import Attachment from './attachmentModel';
import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import EmailTemplate from './emailTemplateModel';
import IDate from '@dvrd/idate';
import {ProductSelection} from "../util/types";
import {ProductSelectionEnum} from "../util/enums";
import {DEFAULT_PRODUCT_SELECTION} from "../util/constants";

interface Props extends BaseProps {
    label?: string;
    flow?: EmailFlow;
    customer_type?: CustomerType;
    days?: number;
    template_id?: string;
    with_invoice?: boolean;
    countries?: string[];
    active?: boolean;
    num_sent?: number;
    num_open?: number;
    num_click?: number;
    only_work_days?: boolean;
    flex?: Record<string, any>;
    product_selection?: ProductSelectionEnum;
    product_selection_json?: ProductSelection;
    account_id?: string;
    account?: Account | any;
    attachments?: (Attachment | any)[];
    fulfilment?: Fulfilment;
    template?: EmailTemplate | any | null;
    num_products?: number;

    // Admin fields
    active_since?: string | null;
    activity?: Array<EmailActivity>;
}

export interface EmailStepFields extends BaseFields {
    label: string;
    flow: EmailFlow;
    customer_type: CustomerType;
    days: number;
    template_id: string;
    with_invoice: boolean;
    countries: string[];
    active: boolean;
    num_sent: number;
    num_open: number;
    num_click: number;
    only_work_days: boolean;
    flex: Record<string, any>;
    product_selection: ProductSelectionEnum;
    product_selection_json: ProductSelection;
    account_id: string;
    account: Account | null;
    attachments: Attachment[];
    fulfilment: Fulfilment;
    template: EmailTemplate | null;
    num_products: number;

    // Admin fields
    active_since: string | null;
    activity: Array<EmailActivity>;
}

const fields: EmailStepFields = {
    id: '',
    created_at: '',
    updated_at: '',
    label: '',
    flow: EmailFlow.INVOICE,
    customer_type: CustomerType.BOTH,
    days: 0,
    template_id: '',
    with_invoice: true,
    countries: [],
    active: false,
    num_sent: 0,
    num_open: 0,
    num_click: 0,
    only_work_days: false,
    product_selection: ProductSelectionEnum.ALL,
    product_selection_json: {clauses: [DEFAULT_PRODUCT_SELECTION]},
    account_id: '',
    account: null,
    attachments: [],
    fulfilment: Fulfilment.BOTH,
    template: null,
    flex: {},
    num_products: 0,

    // Admin fields
    active_since: null,
    activity: []
};

const fieldsDefinition: { [key in keyof Omit<EmailStepFields, 'account' | 'attachments' | 'template'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    label: createFieldDef(ModelFieldType.STRING),
    days: createFieldDef(ModelFieldType.NUMBER),
    template_id: createFieldDef(ModelFieldType.STRING),
    with_invoice: createFieldDef(ModelFieldType.BOOLEAN),
    active: createFieldDef(ModelFieldType.BOOLEAN),
    num_sent: createFieldDef(ModelFieldType.NUMBER),
    num_click: createFieldDef(ModelFieldType.NUMBER),
    num_open: createFieldDef(ModelFieldType.NUMBER),
    account_id: createFieldDef(ModelFieldType.STRING),
    customer_type: createFieldDef(ModelFieldType.STRING, CustomerType.BOTH),
    flow: createFieldDef(ModelFieldType.STRING, EmailFlow.INVOICE),
    product_selection: createFieldDef(ModelFieldType.STRING, ProductSelectionEnum.ALL),
    product_selection_json: createFieldDef(ModelFieldType.OBJECT, {clauses: [DEFAULT_PRODUCT_SELECTION]}),
    only_work_days: createFieldDef(ModelFieldType.BOOLEAN),
    countries: createFieldDef(ModelFieldType.ARRAY),
    fulfilment: createFieldDef(ModelFieldType.STRING, Fulfilment.BOTH),
    flex: createFieldDef(ModelFieldType.OBJECT),
    active_since: createFieldDef(ModelFieldType.STRING, null, true),
    activity: createFieldDef(ModelFieldType.ARRAY),
    num_products: createFieldDef(ModelFieldType.NUMBER),
};

const ignoreProps: Array<keyof Props> = ['account', 'attachments'];

export default class EmailStep extends BaseModel<Props, EmailStepFields> {
    productSelectionChanged: boolean;

    static getAll = (accountId: string, search: string | null, callback: ModelResponse<EmailStep[]>) => {
        getEmailSteps({
            data: {account_id: accountId, search},
            callback: (data: ResponseData) => {
                const steps: EmailStep[] = data.success ? data.email_steps.map((step: any) => new EmailStep(step)) : [];
                callback(steps, data.success, data);
            }
        });
    };

    static getAdminAll = (userId: string, accountId: string, callback: ModelResponse<Array<EmailStep>>) => {
        getAdminEmailSteps({
            userId, accountId, callback: (data: ResponseData) => {
                const steps: EmailStep[] = data.success ? data.email_steps.map((step: any) => new EmailStep(step)) : [];
                callback(steps, data.success, data);
            }
        });
    };

    static get = (accountID: string, emailStepID: string, callback: ModelResponse<EmailStep | null>) => {
        return getEmailStep({
            account_id: accountID, email_step_id: emailStepID, callback: (data: ResponseData) => {
                const step = data.success ? new EmailStep(data.email_step) : null;
                callback(step, data.success, data);
            }
        })
    }

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
        this.productSelectionChanged = false;
    }

    get id(): string {
        return this.fields.id as string;
    }

    get label(): string {
        return this.fields.label;
    }

    set label(value: string) {
        this.fields.label = value;
    }

    get flow(): EmailFlow {
        return this.fields.flow;
    }

    set flow(flow: EmailFlow) {
        this.fields.flow = flow;
    }

    get customerType(): CustomerType {
        return this.fields.customer_type;
    }

    set customerType(customer_type: CustomerType) {
        this.fields.customer_type = customer_type;
    }

    get days(): number {
        return this.fields.days;
    }

    set days(days: number) {
        this.fields.days = days;
    }

    get translatedDays(): string {
        const {days} = this;
        if (days === -1) return 'Order geplaatst';
        if (days === 0) return 'Order verzonden';
        if (days === 1) return '1 dag na zending';
        return `${days} dagen na zending`;
    }

    get templateId(): string {
        return this.fields.template_id;
    }

    set templateId(template_id: string) {
        this.fields.template_id = template_id;
    }

    get productSelection(): ProductSelectionEnum {
        return this.fields.product_selection;
    }

    get productSelectionJSON(): ProductSelection {
        return this.fields.product_selection_json;
    }

    set productSelectionJSON(selection: ProductSelection) {
        if (JSON.stringify(selection) === JSON.stringify(this.fields.product_selection_json))
            return
        this.fields.product_selection_json = selection
        this.productSelectionChanged = true;
    }

    get withInvoice(): boolean {
        return this.fields.with_invoice;
    }

    set withInvoice(with_invoice: boolean) {
        this.fields.with_invoice = with_invoice;
    }

    get active(): boolean {
        return this.fields.active;
    }

    set active(active: boolean) {
        this.fields.active = active;
    }

    get numSent(): number {
        return this.fields.num_sent;
    }

    set numSent(num_sent: number) {
        this.fields.num_sent = num_sent;
    }

    get numOpen(): number {
        return this.fields.num_open;
    }

    get numClick(): number {
        return this.fields.num_click;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.fields.account_id = account.id;
    }

    get attachments(): Attachment[] {
        return this.fields.attachments;
    }

    set attachments(attachments: Attachment[]) {
        this.fields.attachments = attachments;
    }

    get onlyWorkDays(): boolean {
        return this.fields.only_work_days;
    }

    set onlyWorkDays(only_work_days: boolean) {
        this.fields.only_work_days = only_work_days;
    }

    get countries(): string[] {
        return this.fields.countries;
    }

    set countries(countries: string[]) {
        this.fields.countries = countries;
    }

    get fulfilment(): Fulfilment {
        return this.fields.fulfilment;
    }

    set fulfilment(fulfilment: Fulfilment) {
        this.fields.fulfilment = fulfilment;
    }

    get template(): EmailTemplate | null {
        return this.fields.template;
    }

    get flex(): Record<string, any> {
        return this.fields.flex;
    }

    set flex(flex: Record<string, any>) {
        this.fields.flex = flex;
    }

    get activeSinceMoment(): IDate | null {
        if (!this.fields.active_since) return null;
        return new IDate(this.fields.active_since);
    }

    get activity(): Array<EmailActivity> {
        return this.fields.activity;
    }

    get numProducts(): number {
        return this.fields.num_products;
    }

    clone = (deep: boolean = true): EmailStep => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new EmailStep(fields);
    };

    getFlex = (key: string, defaultValue: any = null): any => {
        return this.fields.flex[key] ?? defaultValue;
    };

    setFlex = (key: string, value: any) => {
        this.fields.flex[key] = value;
    };

    deleteFlex = (key: string) => {
        delete this.fields.flex[key];
    };

    addAttachment = (attachment: Attachment): EmailStep => {
        this.fields.attachments = this.fields.attachments.concat([attachment]);
        return this;
    };

    removeAttachment = (attachment: Attachment): EmailStep => {
        this.fields.attachments = this.fields.attachments.filter((att: Attachment) => att.id !== attachment.id);
        return this;
    };

    hasAttachment = (attachment: Attachment): boolean => {
        return this.fields.attachments.find((att: Attachment) => att.id === attachment.id) !== undefined;
    };

    save = (callback?: ModelResponse<EmailStep>) => {
        const data: CreateEmailStep = {
            account_id: this.accountId,
            label: this.label,
            active: this.active,
            days: this.days,
            flow: this.flow,
            template_id: nullify(this.templateId),
            with_invoice: this.withInvoice,
            attachment_ids: nullify(this.attachments.map((att: Attachment) => att.id)),
            customer_type: this.customerType,
            only_work_days: this.onlyWorkDays,
            countries: nullify(this.countries),
            fulfilment: this.fulfilment,
            product_selections: this.productSelectionJSON,
        };
        if (this.productSelectionChanged) data.product_selections = this.productSelectionJSON;
        createEmailStep({
            data, callback: (data: ResponseData) => {
                if (data.success) this.build(data.email_step);
                if (callback) callback(this, data.success, data);
            }
        });
    };

    update = (callback?: ModelResponse<EmailStep>) => {
        const data: UpdateEmailStep = {
            label: this.label,
            customer_type: this.customerType,
            days: this.days,
            flow: this.flow,
            attachment_ids: nullify(this.attachments.map((att: Attachment) => att.id)),
            with_invoice: this.withInvoice,
            email_step_id: this.id,
            template_id: nullify(this.templateId),
            active: this.active,
            account_id: this.accountId,
            only_work_days: this.onlyWorkDays,
            countries: nullify(this.countries),
            fulfilment: this.fulfilment
        };
        if (this.productSelectionChanged) data.product_selections = this.productSelectionJSON;
        updateEmailStep({
            data, callback: (data: ResponseData) => {
                if (data.success) this.build(data.email_step);
                if (callback) callback(this, data.success, data);
            }
        });
    };

    delete = (callback?: ModelResponse<EmailStep>) => {
        deleteEmailStep({
            email_step_id: this.id, account_id: this.accountId, callback: (data: ResponseData) => {
                if (callback) callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        this.productSelectionChanged = false;
        if (props) {
            if (props.account) {
                this.account = props.account instanceof Account ? props.account : new Account(props.account);
            }
            if (props.attachments) {
                this.fields.attachments = props.attachments.map(
                    (att: any) => att instanceof Attachment ? att : new Attachment(att));
            }
            if (props.template) {
                this.fields.template = props.template instanceof EmailTemplate ? props.template :
                    new EmailTemplate(props.template);
            }
        }
    };
}